import moment from 'moment';
import {
    NURSE_DEPLOYMENT_TYPE_NURSE,
    NURSE_DEPLOYMENT_TYPE_PAUSE,
    NURSE_DEPLOYMENT_TYPE_PAUSE_FEE_BASED,
    NURSE_DEPLOYMENT_TYPE_CANCELLATION
} from './types';

/** * Counts the number of active nurse deployments based on the provided care needs.
 * * @param {Array} nurseDeployments - Array of nurse deployment objects.
 * @param {Array} careNeeds - Array of care need objects.
 * * @returns {number} - Returns the count of active nurse deployments.
 */
export const checkActiveInctiveBalance = (nurseDeployments, careNeeds) => {
    if (!Array.isArray(nurseDeployments) || nurseDeployments.length === 0) {
        return true; // No deployments to check
    }

    // get all still active careNeeds ids
    const activeCareNeedIds = careNeeds.filter(careNeed => {
        let is_from_based_active = careNeed.careFrom !== null && new Date(careNeed.careFrom) <= new Date();
        let is_till_based_active = careNeed.careTill === null || new Date(careNeed.careTill) >= new Date();
        return (is_from_based_active && is_till_based_active);
    }).map(careNeed => careNeed.id);

    //console.log("checkForDeploymentOverlapping: activeCareNeedIds =>", activeCareNeedIds);
    if (activeCareNeedIds.length === 0) {
        return true;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // split  deployments by careNeedId to get an array of deployments for each careNeedId
    const deploymentsByCareNeed = nurseDeployments.reduce((acc, deployment) => {
        // only consider deployments that are still active for the careNeeds
        if (!activeCareNeedIds.includes(deployment.careNeedId)) {
            return acc; // Skip deployments not in active care needs
        }

        if (!acc[deployment.careNeedId]) {
            acc[deployment.careNeedId] = [];
        }
        acc[deployment.careNeedId].push(deployment);

        return acc;
    }, {});

    let count_nurse_active = 0;
    let count_nurse_inactive = 0;
    //let count_pause_active = 0;
    let count_pause_inactive = 0;

    // sort all deployments by billingFrom date descending
    for (const careNeedId in deploymentsByCareNeed) {
        deploymentsByCareNeed[careNeedId].sort((a, b) => new Date(b.billingFrom || b.careFrom) - new Date(a.billingFrom || a.careFrom));
    }

   // check for active deployments
    for (const careNeedId in deploymentsByCareNeed) {
        const deployments = deploymentsByCareNeed[careNeedId];
        if (deployments.length === 0) {
            continue; // No active deployments for this care need
        }

        const currentDeployment = deployments[0];
        if (currentDeployment.type === NURSE_DEPLOYMENT_TYPE_CANCELLATION) {
            continue; // Skip already canceled deployments
        }
        else if (currentDeployment.type === NURSE_DEPLOYMENT_TYPE_PAUSE || currentDeployment.type === NURSE_DEPLOYMENT_TYPE_PAUSE_FEE_BASED) {
            // make sure the pause is still valid, i.e. the careTill date is not in the past
            const careTillDate = new Date(currentDeployment.careTill);
            if (careTillDate <= today) {
                count_pause_inactive++;
            }
            // else {
            //     count_pause_active++;
            // }
        }
        else if (currentDeployment.type === NURSE_DEPLOYMENT_TYPE_NURSE) {
            // make sure the billingTill date is not in the past
            const billingTillDate = new Date(currentDeployment.billingTill);
            if (billingTillDate <= today) {
                count_nurse_inactive++;
            }else {
                count_nurse_active++;
            }
        }
        else {
            //console.info("Unknown deployment type", currentDeployment.type);
            continue; // Skip unknown deployment types
        }
    }

    if (count_nurse_active>=1)
    {
        if (count_nurse_inactive > 0 || count_pause_inactive > 0) {
            //console.log("inactive deployments: nurse_active =>", count_nurse_active, "nurse_inactive =>", count_nurse_inactive, "pause_active =>", count_pause_active, "pause_inactive =>", count_pause_inactive);
            return false;
        }
    }

    // everything is fine
    return true;

}


/* * Checks if there are overlapping nurse deployments, or not correctly finished deployments
 *
 * @param {Array} nurseDeployments - Array of nurse deployment objects.
 * @returns {boolean} - Returns true if there are overlapping deployments, false otherwise.
 */
export const checkForDeploymentOverlapping = (nurseDeployments, careNeeds) => {
    //console.log("checkForDeploymentOverlapping: careNeeds =>", careNeeds);
    if (!Array.isArray(nurseDeployments) || nurseDeployments.length === 0 || nurseDeployments.length === 1) {
        return false;
    }

    // get all still active careNeeds ids
    const activeCareNeedIds = careNeeds.filter(careNeed => {
        let is_from_based_active = careNeed.careFrom !== null && new Date(careNeed.careFrom) <= new Date();
        let is_till_based_active = careNeed.careTill === null || new Date(careNeed.careTill) >= new Date();
        //console.log("checkForDeploymentOverlapping: careNeed =>", careNeed, "is_from_based_active =>", is_from_based_active, "is_till_based_active =>", is_till_based_active);

        return (is_from_based_active && is_till_based_active);
    }).map(careNeed => careNeed.id);

    //console.log("checkForDeploymentOverlapping: activeCareNeedIds =>", activeCareNeedIds);
    if (activeCareNeedIds.length === 0) {
        return false;
    }

    // split  deployments by careNeedId to get an array of deployments for each careNeedId
    const deploymentsByCareNeed = nurseDeployments.reduce((acc, deployment) => {
        // only consider deployments that are still active for the careNeeds
        if (!activeCareNeedIds.includes(deployment.careNeedId)) {
            return acc; // Skip deployments not in active care needs
        }

        if (!acc[deployment.careNeedId]) {
            acc[deployment.careNeedId] = [];
        }
        acc[deployment.careNeedId].push(deployment);

        return acc;
    }, {});

    // sort all deployments by billingFrom date descinding
    for (const careNeedId in deploymentsByCareNeed) {
        deploymentsByCareNeed[careNeedId].sort((a, b) => new Date(b.billingFrom || b.careFrom) - new Date(a.billingFrom || a.careFrom));
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // check for overlapping deployments within each careNeedId, only check the latest deployment
    let found_overlapping = false;
    for (const careNeedId in deploymentsByCareNeed) {
        const deployments = deploymentsByCareNeed[careNeedId];
        if (deployments.length === 0) {
            //console.log("Skipping careNeedId no deployments", careNeedId);
            continue; // No overlapping possible with less than 2 deployments
        }

        const currentDeployment = deployments[0];
        if (currentDeployment.type === NURSE_DEPLOYMENT_TYPE_CANCELLATION) {
            //console.log("Skipping canceled deployment", currentDeployment.id);
            continue; // Skip already canceled deployments
        }
        if (currentDeployment.type === NURSE_DEPLOYMENT_TYPE_PAUSE || currentDeployment.type === NURSE_DEPLOYMENT_TYPE_PAUSE_FEE_BASED) {
            // make sure the pause is still valid, i.e. the careTill date is not in the past
            const careTillDate = new Date(currentDeployment.careTill);
            // Create today date with time 0:00:00
            if (careTillDate < today) {
                //console.log("Pause is not valid anymore, careTill date is in the past", currentDeployment.careTill);
                found_overlapping = true;
            }
        }
        if (currentDeployment.type === NURSE_DEPLOYMENT_TYPE_NURSE)
        {
            // make sure the billingTill date is not in the past
            const billingTillDate = new Date(currentDeployment.billingTill);
            if (billingTillDate < today) {
                //console.info("Nurse deployment is not valid anymore, billingTill date is in the past", currentDeployment.billingTill);
                found_overlapping = true;  // Overlapping found, nurse deployment is still valid
            }
        }
    }

    return found_overlapping;
}

export const checkLegalGapDays = (nursedeployments, currenNurseDeployment, newbillingFromDate, newDeploymentType) => {

    const prev = getPreviousDeployment(nursedeployments, currenNurseDeployment, newbillingFromDate, newDeploymentType);
    if (prev === null || prev === undefined)
    {
        //console.error("no prev found!!!");
        return { isValid: true };
    }

    const prevBillingTillDate = prev?.transferDepartureDate !== null && prev?.transferDepartureDate?.date > prev?.billingTill ? prev?.transferDepartureDate?.date : prev?.billingTill;
    const prevTillDate = (prev?.type === NURSE_DEPLOYMENT_TYPE_PAUSE || prev?.type === NURSE_DEPLOYMENT_TYPE_PAUSE_FEE_BASED) ? prev?.careTill : prevBillingTillDate;
    const gapDays = countGapDays(prevTillDate, newbillingFromDate);
    //console.log("prev transferDepartureDate =>", prev.transferDepartureDate," billingTill =>", prev.billingTill, "careTill =>", prev.careTill, "type =>", prev.type, " => (updated) billingTill =>", prevBillingTillDate);

    //console.log("gaps => ",gapDays, " start=>",startDate,"end=>",newbillingFromDate,prev.type,"=>",newDeploymentType);
    //console.log("prev.id =>",prev?.id,"prev.type =>",prev?.type,"prev.billingFrom =>",prev?.billingFrom,"prev.careFrom =>",prev?.careFrom,"newbillingFromDate =>");

    // Wenn BK gefolgt von BK: Darf dasselbe Datum sein
    if (prev.type === NURSE_DEPLOYMENT_TYPE_NURSE && newDeploymentType === NURSE_DEPLOYMENT_TYPE_NURSE )
        if (gapDays <= 0 || gapDays === 1) {
            return { isValid: true };
        } else {
            if (gapDays > 0)
                return { isValid: false, message: `Es darf keinen Lückentag zwischen den Einsätzen geben. Lückentage: ${gapDays}! Prev-End: ${prevTillDate} [${prev?.type}] Next-Start: ${newbillingFromDate}` };
            else
                return { isValid: false, message: 'BK überschneidet sich mit einer anderen BK' };
        }

    // Wenn BK gefolgt von Pause: 'Betreuung geplant von' MUSS 'Pause bis' + 1 sein
    // Wenn Pause gefolgt von BK: 'Pause von' MUSS 'Betreuung geplant bis' + 1 sein
    // Wenn Pause gefolgt von Kündigungsfrist: 'Pause von' MUSS 'Kündigungsfrist bis' + 1 sein
    // Wenn BK gefolgt von Kündigungsfrist: 'Betreuung geplant von' MUSS 'Kündigungsfrist bis' + 1 sein
    if (gapDays === 1) {
        return { isValid: true };
    } else {
        if (gapDays > 0)
            return { isValid: false, message: `Es darf keinen Lückentag zwischen den Einsätzen/Pausen etc. geben. Lückentage: ${gapDays}! Prev-End: ${prevTillDate} [${prev?.type}] Next-Start: ${newbillingFromDate}` };
        else
            return { isValid: false, message: `Überschneidet sich mit einem anderem Einsatz/Pause. Lückentage: ${gapDays}! Prev-End: ${prevTillDate}` };
    }
}

const countGapDays = (startDate, endDate) => {
    if (!startDate || !endDate)
        return 0;
    const start = moment(startDate);
    const end = moment(endDate);

    return end.diff(start, 'days');
};

const getPreviousDeployment = (deployments, currenNurseDeployment, dateStart, newType) => {
    if (!Array.isArray(deployments) || deployments.length === 0)
        return null;

    const parsedDateStart = new Date(dateStart);
    const excludedId = currenNurseDeployment?.id;

    // Sort deployments by billingTill ascending before filtering
    const sortedDeployments = deployments.slice().sort((a, b) => {
        const aEnd = (a.type === NURSE_DEPLOYMENT_TYPE_PAUSE || a.type === NURSE_DEPLOYMENT_TYPE_PAUSE_FEE_BASED)
            ? new Date(a.careTill)
            : new Date(a.billingTill);
        const bEnd = (b.type === NURSE_DEPLOYMENT_TYPE_PAUSE || b.type === NURSE_DEPLOYMENT_TYPE_PAUSE_FEE_BASED)
            ? new Date(b.careTill)
            : new Date(b.billingTill);
        return aEnd - bEnd; // Ascending
    });

    //console.log("getPreviousDeployment: sortedDeployments", sortedDeployments);

    let deploymenttoCheck =  sortedDeployments.filter(dep => {
            if (dep.id === excludedId)
                return false;

            // Use the correct end date for each type
            const depEndDate = (dep.type === NURSE_DEPLOYMENT_TYPE_PAUSE || dep.type === NURSE_DEPLOYMENT_TYPE_PAUSE_FEE_BASED)
                ? new Date(dep.careTill)
                : new Date(dep.billingTill);
            const depStartDate = (dep.type === NURSE_DEPLOYMENT_TYPE_PAUSE || dep.type === NURSE_DEPLOYMENT_TYPE_PAUSE_FEE_BASED)
                ? new Date(dep.careFrom)
                : new Date(dep.billingFrom);

            // Only consider deployments that are in the time range of the new deployment 
            return depEndDate < parsedDateStart || depStartDate < parsedDateStart;
        });

    let sortedFilteredDeployments = deploymenttoCheck.sort((a, b) => {
        const aEnd = (a.type === NURSE_DEPLOYMENT_TYPE_PAUSE || a.type === NURSE_DEPLOYMENT_TYPE_PAUSE_FEE_BASED)
            ? new Date(a.careTill)
            : new Date(a.billingTill);
        const bEnd = (b.type === NURSE_DEPLOYMENT_TYPE_PAUSE || b.type === NURSE_DEPLOYMENT_TYPE_PAUSE_FEE_BASED)
            ? new Date(b.careTill)
            : new Date(b.billingTill);
        return bEnd - aEnd; // Descending
    });

    //console.log("getPreviousDeployment: sortedFilteredDeployments", sortedFilteredDeployments);

    return sortedFilteredDeployments[0] || null; // Most recent past deployment
};
